import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Stack,
  Paper,
  Typography,
  Chip,
  TextField,
  InputAdornment,
  Button,
  Divider,
  LinearProgress,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  Drawer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Skeleton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
} from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import InfoIcon from "@mui/icons-material/Info";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import StopIcon from "@mui/icons-material/Stop";
import AddTaskIcon from "@mui/icons-material/TaskAlt";
import EditIcon from "@mui/icons-material/Edit";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ImageIcon from "@mui/icons-material/Image";
import AssessmentIcon from "@mui/icons-material/Assessment";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import DoneIcon from "@mui/icons-material/Done";

import { UserContext } from "../../../context/provider/UserProvider";
import { Fetch } from "../../../services/api"; // lo mantengo para los endpoints de Proyect/*
import { notify } from "../../../utils/utils";

/* ===================== Config ===================== */
const API_BASE = "https://xprojectwebapi.abexacloud.com/api";

const ENDPOINT_DETAIL = (codTicket) =>
  `${API_BASE}/Proyect/detailed?` + new URLSearchParams({ CodTicket: String(codTicket) });

const ENDPOINT_ANALYSIS = (codTicket) =>
  `${API_BASE}/Proyect/analysis?` + new URLSearchParams({ CodTicket: String(codTicket) });

const ENDPOINT_IDEA = (codTicket) =>
  `${API_BASE}/Proyect/idea?` + new URLSearchParams({ CodTicket: String(codTicket) });

/* ===================== Fetch unificado ===================== */
async function fetchJson(url, { method = "GET", body } = {}) {
  const opts = {
    method,
    headers: { accept: "text/plain" },
    cache: "no-cache",
  };
  if (method !== "GET" && body) {
    opts.headers["Content-Type"] = "application/json";
    opts.body = JSON.stringify(body);
  }
  const resp = await fetch(url, opts);
  let json = {};
  try {
    json = await resp.json();
  } catch {
    // noop
  }
  if (!resp.ok || json?.isValid === false) {
    const msg =
      json?.exceptions?.[0]?.description ||
      json?.content ||
      `HTTP ${resp.status}`;
    throw new Error(msg);
  }
  return json;
}

/* ===================== Helpers ===================== */
const toInputDate = (ddmmyyyy) => {
  if (!ddmmyyyy || !ddmmyyyy.includes("/")) return "";
  const [dd, mm, yyyy] = ddmmyyyy.split("/");
  return `${yyyy}-${mm}-${dd}`;
};
const fromInputDate = (yyyy_mm_dd) => {
  if (!yyyy_mm_dd || !yyyy_mm_dd.includes("-")) return "";
  const [yyyy, mm, dd] = yyyy_mm_dd.split("-");
  return `${dd}/${mm}/${yyyy}`;
};

function statusChipColor(nomEstado) {
  const v = (nomEstado || "").toUpperCase();
  if (v.includes("FINAL"))
    return { bg: "#10291f", bd: "#1a5438", tx: "#e7fff0", label: "Finalizado", key: "final" };
  if (v.includes("PAUSA"))
    return { bg: "#2b2620", bd: "#5b4a2f", tx: "#ffeacc", label: "En pausa", key: "pausa" };
  if (v.includes("PROCE"))
    return { bg: "#102133", bd: "#2b4a7a", tx: "#e6f1ff", label: "En proceso", key: "proceso" };
  return { bg: "#2b2333", bd: "#5a3b7a", tx: "#f2e6ff", label: "Pendiente", key: "pendiente" };
}

function groupActivities(rows) {
  const out = {};
  for (const r of rows) {
    const tKey = `${r.codTicket}__${r.nomTicket}`;
    const eKey = `${r.codTicketEntregable}__${r.nomEntregable}`;
    if (!out[tKey]) {
      out[tKey] = {
        codTicket: r.codTicket,
        nomTicket: r.nomTicket,
        objetivo: r.objetivo,
        alcance: r.alcance,
        entregables: {},
      };
    }
    if (!out[tKey].entregables[eKey]) {
      out[tKey].entregables[eKey] = {
        codTicketEntregable: r.codTicketEntregable,
        nomEntregable: r.nomEntregable,
        actividades: [],
      };
    }
    out[tKey].entregables[eKey].actividades.push(r);
  }
  return out;
}



    // Estado general por ticket considerando sus actividades
function computeTicketState(ticket) {
  const acts = Object.values(ticket.entregables).flatMap((e) => e.actividades || []);
  if (!acts.length) return statusChipColor(""); // Pendiente
  let hasRunning = false;
  let hasPaused = false;
  let allFinal = true;
  for (const a of acts) {
    const st = statusChipColor(a.nomEstadoActividad).key;
    if (st !== "final") allFinal = false;
    if (st === "proceso") hasRunning = true;
    if (st === "pausa") hasPaused = true;
  }
  if (allFinal) return statusChipColor("FINALIZADO");
  if (hasPaused) return statusChipColor("PAUSA");
  if (hasRunning) return statusChipColor("PROCESO");
  return statusChipColor("PENDIENTE");
}

const darkPaper = {
  bgcolor: "#0b0f14",
  border: "1px solid #2c3442",
  color: "#e5e9f0",
  boxShadow: "0 6px 24px rgba(0,0,0,.35)",
  borderRadius: 5,
};

const fieldOutline = { "& fieldset": { borderColor: "#3a4457" } };

// Detecta si la URL apunta a imagen (para miniatura)
const isImageUrl = (url = "", ext = "") => {
  const e = (ext || "").toLowerCase();
  const byExt = ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"].includes(e);
  const byPath = /\/verImagen/i.test(url) || /\.(png|jpe?g|gif|webp|bmp|svg)(\?.*)?$/i.test(url || "");
  return byExt || byPath;
};

/* ===================== Componente ===================== */
export const Tareasv2 = () => {
  const { stateUser } = useContext(UserContext);
  const codUsuario = Number(stateUser?.codUsuario || stateUser?.codUsuarioAccion || 0);

    // Helpers fecha (dd/mm/yyyy)
    const pad = (v) => String(v).padStart(2, "0");
    const toDDMMYYYY = (date) => {
    const d = date instanceof Date ? date : new Date(date);
    return `${pad(d.getDate())}/${pad(d.getMonth()+1)}/${d.getFullYear()}`;
    };
    const firstDayOfThisMonth = () => {
    const d = new Date();
    return `01/${pad(d.getMonth()+1)}/${d.getFullYear()}`;
    };
    const today = toDDMMYYYY(new Date());

    // Estado inicial
    const [desde, setDesde] = useState("01/05/2025");
    const [hasta, setHasta] = useState(today);


  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");

  // Filtro de estado multiselección
  const [statusSelected, setStatusSelected] = useState([]); // ["pendiente","proceso","pausa","final"]

  // Data actividades
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [pauseIdMap, setPauseIdMap] = useState({});

  // MODAL Detalle Ticket
  const [openTicketModal, setOpenTicketModal] = useState(false);
  const [ticketSel, setTicketSel] = useState(null);

  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailData, setDetailData] = useState(null); // Proyect/detailed -> content

  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [analysisData, setAnalysisData] = useState([]); // Proyect/analysis -> content[]

  const [loadingIdea, setLoadingIdea] = useState(false);
  const [ideaData, setIdeaData] = useState(null); // Proyect/idea -> content

  // Drawer Actividad
  const [openActDrawer, setOpenActDrawer] = useState(false);
  const [actSel, setActSel] = useState(null);
  const [actTasks, setActTasks] = useState([]);
  const [newTask, setNewTask] = useState("");
  const [editTaskId, setEditTaskId] = useState(null);
  const [editTaskTxt, setEditTaskTxt] = useState("");
  const [loadingTasks, setLoadingTasks] = useState(false);

  // Debounce buscador
  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setQDebounced(q.trim().toLowerCase()), 250);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  // Listado base
  const fetchActivities = useCallback(async () => {
    if (!codUsuario) {
      notify("No hay usuario en sesión", "warning");
      return;
    }
    setLoading(true);
    try {
      const url = `${API_BASE}/Activity/userDateRange?${new URLSearchParams({
        CodUsuarioAccion: String(codUsuario),
        FechaInicio: desde,
        FechaFin: hasta,
      }).toString()}`;
      const json = await fetchJson(url);
      const content = Array.isArray(json.content) ? json.content : [];
      setRows(content);
      const pmap = {};
      for (const a of content) pmap[a.codTicketActividad] = a.codTicketActividadPausa || null;
      setPauseIdMap(pmap);
    } catch (err) {
      notify(String(err?.message || err), "error");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [codUsuario, desde, hasta]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const groupedBase = useMemo(() => groupActivities(rows), [rows]);

  // KPIs por estado de actividades (para chips de referencia)
  const kpis = useMemo(() => {
    let pend = 0, proc = 0, paus = 0, fin = 0;
    for (const r of rows) {
      const s = statusChipColor(r.nomEstadoActividad).key;
      if (s === "pendiente") pend++;
      else if (s === "proceso") proc++;
      else if (s === "pausa") paus++;
      else if (s === "final") fin++;
    }
    return { pend, proc, paus, fin, total: rows.length };
  }, [rows]);

  // Filtrado por buscador + estado general del ticket (multi)
  const grouped = useMemo(() => {
    const term = qDebounced;
    const hasFilter = Array.isArray(statusSelected) && statusSelected.length > 0;

    const out = {};
    for (const tKey of Object.keys(groupedBase)) {
      const t = groupedBase[tKey];

      const tState = computeTicketState(t).key;
      const statusOk = !hasFilter || statusSelected.includes(tState);

      const tMatch =
        !term || t.nomTicket.toLowerCase().includes(term) || String(t.codTicket).includes(term);

      if (statusOk && tMatch) {
        if (!term) {
          out[tKey] = t;
        } else {
          const newEnt = {};
          let anyKeep = false;
          for (const eKey of Object.keys(t.entregables)) {
            const e = t.entregables[eKey];
            const eMatch =
              e.nomEntregable.toLowerCase().includes(term) ||
              String(e.codTicketEntregable).includes(term);
            const acts = e.actividades.filter(
              (a) =>
                a.nomActividad.toLowerCase().includes(term) ||
                String(a.codTicketActividad).includes(term) ||
                (a.nomEstadoActividad || "").toLowerCase().includes(term)
            );
            if (eMatch || acts.length) {
              newEnt[eKey] = { ...e, actividades: eMatch ? e.actividades : acts };
              anyKeep = true;
            }
          }
          if (anyKeep) out[tKey] = { ...t, entregables: newEnt };
        }
      }
    }
    return out;
  }, [groupedBase, qDebounced, statusSelected]);

  /* ===================== Acciones Actividad ===================== */
  const startActivity = useCallback(async (a, e) => {
    e?.stopPropagation?.();
    try {
      const url = `${API_BASE}/Activity/start`;
      await fetchJson(url, {
        method: "POST",
        body: {
          codUsuarioAccion: codUsuario,
          codTicketActividad: a.codTicketActividad,
          fechaAccion: new Date().toISOString(),
        },
      });
      notify("Actividad iniciada", "success");
      fetchActivities();
    } catch (err) {
      notify(String(err?.message || err), "error");
    }
  }, [codUsuario, fetchActivities]);

  const finishActivity = useCallback(async (a, e) => {
    e?.stopPropagation?.();
    try {
      const url = `${API_BASE}/Activity/start`; // mismo endpoint para finalizar
      await fetchJson(url, {
        method: "POST",
        body: {
          codUsuarioAccion: codUsuario,
          codTicketActividad: a.codTicketActividad,
          fechaAccion: new Date().toISOString(),
        },
      });
      notify("Actividad finalizada", "success");
      fetchActivities();
    } catch (err) {
      notify(String(err?.message || err), "error");
    }
  }, [codUsuario, fetchActivities]);

  // Iniciar pausa (UI: Pausar)
  const pauseActivity = useCallback(async (a, e) => {
    e?.stopPropagation?.();
    try {
      const url = `${API_BASE}/Activity/pause/start`;
      await fetchJson(url, {
        method: "PUT",
        body: {
          CodUsuarioAccion: codUsuario,
          CodTicketActividad: a.codTicketActividad,
          codTicketActividadPausaTipo: 1,
        },
      });
      notify("Pausa iniciada", "success");
      fetchActivities();
    } catch (err) {
      notify(String(err?.message || err), "error");
    }
  }, [codUsuario, fetchActivities]);

  // Finalizar pausa (UI: Reanudar)
  const resumeActivity = useCallback(async (a, e) => {
    e?.stopPropagation?.();
    try {
      const pauseId = pauseIdMap[a.codTicketActividad];
      const url = `${API_BASE}/Activity/pause/final`;
      await fetchJson(url, {
        method: "PUT",
        body: {
          CodUsuarioAccion: codUsuario, // Codigo del usuario que realiza la reanudacion
          CodTicketActividad: a.codTicketActividad, // Codigo de la actividad
          CodTicketActividadPausaTipo: null   // Se esta enviando un valor nulo para la reanudacion de la actividad
        },
      });
      notify("Pausa finalizada", "success");
      fetchActivities();
    } catch (err) {
      notify(String(err?.message || err), "error");
    }
  }, [codUsuario, pauseIdMap, fetchActivities]);

  /* ===================== Tareas (Drawer de Actividad) ===================== */
  const loadActTasks = useCallback(async (codTicketActividad) => {
    setLoadingTasks(true);
    try {
      const url = `${API_BASE}/Task?` + new URLSearchParams({ CodTicketActividad: String(codTicketActividad) });
      const json = await fetchJson(url);
      setActTasks(Array.isArray(json.content) ? json.content : []);
    } catch (err) {
      notify(String(err?.message || err), "error");
      setActTasks([]);
    } finally {
      setLoadingTasks(false);
    }
  }, []);

  const canRegisterTasks = useCallback((a) => {
    if (!a) return false;
    const st = statusChipColor(a.nomEstadoActividad).key;
    const running = a.fechaInicioReal && !a.fechaFinReal;
    const paused = pauseIdMap[a.codTicketActividad];
    // Solo actividades EN PROCESO (no pausadas, no finalizadas)
    return st === "proceso" && running && !paused;
  }, [pauseIdMap]);

  const createActTask = useCallback(async () => {
    if (!actSel || !canRegisterTasks(actSel)) {
      notify("Solo puedes registrar tareas en actividades EN PROCESO.", "warning");
      return;
    }
    const txt = (newTask || "").trim();
    if (!txt) return;
    try {
      const url = `${API_BASE}/Task`;
      await fetchJson(url, {
        method: "POST",
        body: {
          codUsuarioAccion: codUsuario,
          codTicketActividad: actSel.codTicketActividad,
          descripcionTarea: txt,
        },
      });
      setNewTask("");
      notify("Tarea registrada", "success");
      loadActTasks(actSel.codTicketActividad);
    } catch (err) {
      notify(String(err?.message || err), "error");
    }
  }, [actSel, newTask, canRegisterTasks, codUsuario, loadActTasks]);

  const updateActTask = useCallback(async () => {
    const txt = (editTaskTxt || "").trim();
    const id = editTaskId;
    if (!id || !txt || !actSel) return;
    try {
      const url = `${API_BASE}/Task`;
      await fetchJson(url, {
        method: "PUT",
        body: { codUsuarioAccion: codUsuario, codTicketTarea: id, descripcionTarea: txt },
      });
      notify("Tarea actualizada", "success");
      setEditTaskId(null);
      setEditTaskTxt("");
      loadActTasks(actSel.codTicketActividad);
    } catch (err) {
      notify(String(err?.message || err), "error");
    }
  }, [actSel, editTaskId, editTaskTxt, codUsuario, loadActTasks]);

  /* ===================== MODAL: Detalle de Ticket ===================== */
  const openTicketDetail = useCallback(async (ticket, e) => {
    e?.stopPropagation?.();
    setTicketSel(ticket);
    setDetailData(null);
    setAnalysisData([]);
    setIdeaData(null);
    setOpenTicketModal(true);

    // 1) IDEA (primero)
    setLoadingIdea(true);
    try {
      const resp = await Fetch({ url: ENDPOINT_IDEA(ticket.codTicket) });
      setIdeaData(resp?.isValid ? (resp.content || null) : null);
    } catch {
      setIdeaData(null);
    } finally {
      setLoadingIdea(false);
    }

    // 2) DETALLE
    setLoadingDetail(true);
    try {
      const resp = await Fetch({ url: ENDPOINT_DETAIL(ticket.codTicket) });
      if (!resp?.isValid) {
        notify(resp?.exceptions?.[0]?.description || "No se pudo obtener el detalle", "error");
      } else {
        setDetailData(resp.content || null);
      }
    } catch (err) {
      notify(String(err?.message || err), "error");
    } finally {
      setLoadingDetail(false);
    }

    // 3) ANALYSIS
    setLoadingAnalysis(true);
    try {
      const resp = await Fetch({ url: ENDPOINT_ANALYSIS(ticket.codTicket) });
      setAnalysisData(resp?.isValid ? (Array.isArray(resp.content) ? resp.content : []) : []);
    } catch {
      setAnalysisData([]);
    } finally {
      setLoadingAnalysis(false);
    }
  }, []);

  const closeTicketDetail = useCallback(() => {
    setOpenTicketModal(false);
    setTicketSel(null);
    setDetailData(null);
    setAnalysisData([]);
    setIdeaData(null);
  }, []);

  const openActivityDetail = useCallback((activity, e) => {
    e?.stopPropagation?.();
    setActSel(activity);
    setOpenActDrawer(true);
    loadActTasks(activity.codTicketActividad);
  }, [loadActTasks]);

  const closeActivityDetail = useCallback(() => {
    setOpenActDrawer(false);
    setActSel(null);
    setActTasks([]);
    setNewTask("");
    setEditTaskId(null);
    setEditTaskTxt("");
  }, []);

  // Chips estilo toggle (marcados, multi)
  const chipToggleSx = (active, bg, bd) => ({
    bgcolor: active ? bg : "#111624",
    color: "#ffffff",
    border: `1px solid ${active ? bd : "#3a4457"}`,
    fontWeight: 800,
    height: 30,
    "& .MuiChip-label": { color: "#ffffff" },
  });

  const toggleStatus = useCallback((key) => {
    setStatusSelected((prev) => {
      const set = new Set(prev || []);
      if (set.has(key)) set.delete(key);
      else set.add(key);
      return Array.from(set);
    });
  }, []);

  // Unifica archivos e imágenes de Detalle + Análisis + Idea en un solo arreglo
  const unifiedFiles = useMemo(() => {
    const out = [];
    const pushFile = (f, kind, source) => {
      if (!f) return;
      out.push({
        key: `${source}-${f.codTicketArchivo || f.nomArchivo || Math.random()}`,
        name: f.nomArchivo || (f.extensionArchivo ? `archivo.${f.extensionArchivo}` : "archivo"),
        url: f.urlArchivo,
        ext: (f.extensionArchivo || "").toLowerCase(),
        kind, // "archivo" | "imagen"
        source, // "Detalle" | "Análisis" | "Idea"
      });
    };

    // Detalle
    if (detailData) {
      (detailData.parametrosArchivo || []).forEach((f) => pushFile(f, "archivo", "Detalle"));
      (detailData.parametrosImagen || []).forEach((f) => pushFile(f, "imagen", "Detalle"));
    }
    // Análisis
    (analysisData || []).forEach((an) => {
      (an.parametrosArchivo || []).forEach((f) => pushFile(f, "archivo", "Análisis"));
      (an.parametrosImagen || []).forEach((f) => pushFile(f, "imagen", "Análisis"));
    });
    // Idea
    if (ideaData) {
      (ideaData.parametrosArchivo || []).forEach((f) => pushFile(f, "archivo", "Idea"));
      (ideaData.parametrosImagen || []).forEach((f) => pushFile(f, "imagen", "Idea"));
    }

    return out;
  }, [detailData, analysisData, ideaData]);

  // Enter para agregar tarea rápidamente
  const onKeyDownAddTask = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      createActTask();
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, minHeight: "90vh", color: "#e5e9f0", bgcolor: "transparent" }}>
      {/* Header / Filtros */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, ...darkPaper }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ xs: "stretch", md: "center" }}>
          <Typography variant="h6" fontWeight={900} sx={{ flex: 1, color: "#e5e9f0" }}>
            Panel de Actividades por Ticket
          </Typography>

          <TextField
            type="date"
            label="Desde"
            size="small"
            value={toInputDate(desde)}
            onChange={(e) => setDesde(fromInputDate(e.target.value))}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 180, ...fieldOutline, "& .MuiInputBase-input": { color: "#e5e9f0" }, "& .MuiInputLabel-root": { color: "#cfd8e3" } }}
          />

          <TextField
            type="date"
            label="Hasta"
            size="small"
            value={toInputDate(hasta)}
            onChange={(e) => setHasta(fromInputDate(e.target.value))}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 180, ...fieldOutline, "& .MuiInputBase-input": { color: "#e5e9f0" }, "& .MuiInputLabel-root": { color: "#cfd8e3" } }}
          />

          <TextField
            placeholder="Buscar: ticket, entregable, actividad…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: "#ffffff" }} />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 280, maxWidth: 420, ...fieldOutline, "& .MuiInputBase-input": { color: "#e5e9f0" } }}
          />

          <Tooltip title="Recargar">
            <IconButton onClick={fetchActivities} sx={{ color: "#ffffff", border: "1px solid #3a4457", borderRadius: 2 }}>
              <RefreshIcon sx={{ color: "#ffffff" }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Paper>

      {/* Chips de filtro por estado (multiselección, marcados) */}
      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap" }}>
        <Chip
          icon={statusSelected.length === 0 ? <DoneIcon sx={{ color: "#ffffff" }} /> : null}
          label={`Todos: ${kpis.total}`}
          onClick={() => setStatusSelected([])}
          sx={chipToggleSx(statusSelected.length === 0, "#141a24", "#3a4457")}
        />
        <Chip
          icon={statusSelected.includes("pendiente") ? <DoneIcon sx={{ color: "#ffffff" }} /> : null}
          label={`Pendientes: ${kpis.pend}`}
          onClick={() => toggleStatus("pendiente")}
          sx={chipToggleSx(statusSelected.includes("pendiente"), "#2b2333", "#5a3b7a")}
        />
        <Chip
          icon={statusSelected.includes("proceso") ? <DoneIcon sx={{ color: "#ffffff" }} /> : null}
          label={`En proceso: ${kpis.proc}`}
          onClick={() => toggleStatus("proceso")}
          sx={chipToggleSx(statusSelected.includes("proceso"), "#102133", "#2b4a7a")}
        />
        <Chip
          icon={statusSelected.includes("pausa") ? <DoneIcon sx={{ color: "#ffffff" }} /> : null}
          label={`En pausa: ${kpis.paus}`}
          onClick={() => toggleStatus("pausa")}
          sx={chipToggleSx(statusSelected.includes("pausa"), "#2b2620", "#5b4a2f")}
        />
        <Chip
          icon={statusSelected.includes("final") ? <DoneIcon sx={{ color: "#ffffff" }} /> : null}
          label={`Finalizadas: ${kpis.fin}`}
          onClick={() => toggleStatus("final")}
          sx={chipToggleSx(statusSelected.includes("final"), "#10291f", "#1a5438")}
        />
      </Stack>

      {loading && (
        <LinearProgress
          sx={{ mb: 2, borderRadius: 2, bgcolor: "#141a24", "& .MuiLinearProgress-bar": { bgcolor: "#6aa3ff" } }}
        />
      )}

      {/* Contenido agrupado con TICKET como desplegable */}
      <Stack spacing={2}>
        {Object.keys(grouped).length === 0 && !loading && (
          <Paper sx={{ p: 3, ...darkPaper }}>
            <Typography variant="body1" sx={{ color: "#dfe7ff" }}>
              No hay actividades en el rango/filtro seleccionado.
            </Typography>
          </Paper>
        )}

        {Object.keys(grouped).map((tKey) => {
          const t = grouped[tKey];
          const entregas = Object.keys(t.entregables);
          const tState = computeTicketState(t);

          return (
            <Accordion key={tKey} disableGutters sx={{ ...darkPaper }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#ffffff" }} />}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  alignItems={{ xs: "flex-start", md: "center" }}
                  justifyContent="space-between"
                  sx={{ width: "100%" }}
                  spacing={1}
                >
                  <Stack direction="row" alignItems="center" gap={1} sx={{ flexWrap: "wrap" }}>
                    <Typography variant="subtitle1" fontWeight={900} sx={{ color: "#e5e9f0" }}>
                      #{t.codTicket}
                    </Typography>
                    <Typography variant="h6" fontWeight={900} sx={{ color: "#e5e9f0" }}>
                      {t.nomTicket}
                    </Typography>
                    <Chip
                      size="small"
                      label={`${entregas.length} entregable(s)`}
                      sx={{ ml: 1, bgcolor: "#111624", color: "#ffffff", border: "1px solid #3a4457" }}
                    />
                    <Chip
                      size="small"
                      label={tState.label}
                      sx={{ ml: 1, bgcolor: tState.bg, color: tState.tx, border: `1px solid ${tState.bd}` }}
                    />
                  </Stack>

                  <Tooltip title="Ver detalle del ticket (idea, descripción, análisis, archivos)">
                    <IconButton
                      onClick={(e) => openTicketDetail(t, e)}
                      sx={{
                        color: "#ffffff",
                        border: "1px solid #3a4457",
                        borderRadius: 2,
                        bgcolor: "#141a24",
                        "&:hover": { bgcolor: "#161d2a" },
                      }}
                    >
                      <InfoOutlinedIcon sx={{ color: "#ffffff" }} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </AccordionSummary>

              <AccordionDetails sx={{ borderTop: "1px dashed #2c3442" }}>
                {entregas.map((eKey) => {
                  const e = t.entregables[eKey];
                  return (
                    <Box key={eKey} sx={{ mb: 1.25 }}>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ color: "#e5e9f0", mb: 0.5 }}>
                        Entregable #{e.codTicketEntregable} — {e.nomEntregable}
                      </Typography>

                      <Stack spacing={1}>
                        {e.actividades.map((a) => {
                          const s = statusChipColor(a.nomEstadoActividad);
                          const running = a.fechaInicioReal && !a.fechaFinReal;
                          const paused = pauseIdMap[a.codTicketActividad];
                          const isFinal = s.key === "final";

                          return (
                            <Paper
                              key={a.codTicketActividad}
                              sx={{
                                p: 1,
                                bgcolor: "#0f141c",
                                border: "1px solid #2c3442",
                                borderRadius: 3,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 1,
                              }}
                            >
                              <Stack
                                direction={{ xs: "column", md: "row" }}
                                spacing={1}
                                alignItems={{ xs: "flex-start", md: "center" }}
                                sx={{ color: "#e5e9f0" }}
                              >
                                <Typography
                                  variant="body2"
                                  fontWeight={800}
                                  sx={{ cursor: "pointer", color: "#dfe7ff" }}
                                  onClick={(e) => openActivityDetail(a, e)}
                                  title="Ver detalle de la actividad"
                                >
                                  [{a.codTicketActividad}] {a.nomActividad}
                                </Typography>
                                <Chip size="small" label={s.label} sx={{ bgcolor: s.bg, color: s.tx, border: `1px solid ${s.bd}` }} />
                                <Chip
                                  size="small"
                                  label={`Plan: ${a.fechaInicioProgramado || "—"} → ${a.fechaFinProgramado || "—"}`}
                                  sx={{ bgcolor: "#101521", color: "#ffffff", border: "1px solid #2c3442" }}
                                />
                                <Chip
                                  size="small"
                                  label={`Real: ${a.fechaInicioReal || "—"} → ${a.fechaFinReal || "—"}`}
                                  sx={{ bgcolor: "#101521", color: "#ffffff", border: "1px solid #2c3442" }}
                                />
                              </Stack>

                              {/* Acciones según estado */}
                              <Stack direction="row" gap={1} onClick={(e) => e.stopPropagation()}>
                                {!isFinal && !running && (
                                  <Tooltip title="Iniciar actividad">
                                    <span>
                                      <Button
                                        size="small"
                                        variant="contained"
                                        onClick={(e) => startActivity(a, e)}
                                        startIcon={<PlayArrowIcon sx={{ color: "#ffffff" }} />}
                                      >
                                        Iniciar
                                      </Button>
                                    </span>
                                  </Tooltip>
                                )}

                                {!isFinal && running && !paused && (
                                  <>
                                    <Tooltip title="Pausar (iniciar pausa)">
                                      <span>
                                        <Button
                                          size="small"
                                          color="warning"
                                          variant="contained"
                                          onClick={(e) => pauseActivity(a, e)}
                                          startIcon={<PauseIcon sx={{ color: "#ffffff" }} />}
                                        >
                                          Pausar
                                        </Button>
                                      </span>
                                    </Tooltip>
                                    <Tooltip title="Finalizar actividad">
                                      <span>
                                        <Button
                                          size="small"
                                          color="success"
                                          variant="contained"
                                          onClick={(e) => finishActivity(a, e)}
                                          startIcon={<StopIcon sx={{ color: "#ffffff" }} />}
                                        >
                                          Finalizar
                                        </Button>
                                      </span>
                                    </Tooltip>
                                  </>
                                )}

                                {!isFinal && running && paused && (
                                  <Tooltip title="Reanudar (finalizar pausa)">
                                    <span>
                                      <Button
                                        size="small"
                                        color="secondary"
                                        variant="contained"
                                        onClick={(e) => resumeActivity(a, e)}
                                        startIcon={<PlayArrowIcon sx={{ color: "#ffffff" }} />}
                                      >
                                        Reanudar
                                      </Button>
                                    </span>
                                  </Tooltip>
                                )}

                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={(e) => openActivityDetail(a, e)}
                                  startIcon={<PlaylistAddIcon sx={{ color: "#ffffff" }} />}
                                  sx={{ borderColor: "#3a4457", color: "#ffffff" }}
                                >
                                  Ver / Tareas
                                </Button>
                              </Stack>
                            </Paper>
                          );
                        })}
                      </Stack>
                    </Box>
                  );
                })}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Stack>

      {/* Footer */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 2 }}>
        <Typography variant="body2" sx={{ color: "#dfe7ff" }}>
          {loading ? "Cargando…" : `Mostrando ${rows.length} actividad(es)`}
        </Typography>
        <Typography variant="caption" sx={{ color: "#cfd8e3" }}>
          Usuario: {stateUser?.nomUsuario || stateUser?.userName || codUsuario || "—"}
        </Typography>
      </Stack>

      {/* ===================== MODAL Detalle Ticket ===================== */}
      <Dialog
        open={openTicketModal}
        onClose={closeTicketDetail}
        PaperProps={{ sx: { width: 980, maxWidth: "96vw", bgcolor: "#0b0f14", border: "1px solid #2c3442", color: "#e5e9f0", borderRadius: 3 } }}
      >
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ color: "#e5e9f0" }}>
            <InfoIcon sx={{ color: "#ffffff" }} />
            <span>Detalle del Ticket</span>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: "#2c3442" }}>
          <Typography variant="body2" sx={{ color: "#dfe7ff", mb: 1 }}>
            Ticket {ticketSel?.codTicket} — {ticketSel?.nomTicket}
          </Typography>

          {/* ========= 1) Idea ========= */}
          <Accordion defaultExpanded disableGutters sx={{ bgcolor: "#0f141c", border: "1px solid #2c3442", borderRadius: 2, mb: 1.25 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#ffffff" }} />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <LightbulbOutlinedIcon fontSize="small" sx={{ color: "#ffffff" }} />
                <Typography variant="subtitle2" fontWeight={800} sx={{ color: "#e5e9f0" }}>Idea</Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              {loadingIdea ? (
                <>
                  <Skeleton variant="text" sx={{ bgcolor: "#1a2030" }} />
                  <Skeleton variant="rectangular" height={48} sx={{ bgcolor: "#1a2030", borderRadius: 2 }} />
                </>
              ) : (
                <Stack spacing={0.75} sx={{ color: "#e5e9f0" }}>
                  <Typography variant="body2"><strong>Título:</strong> {ideaData?.nomTicketIdea || "—"}</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    <strong>Contenido:</strong> {ideaData?.contenidoTicketIdea || "—"}
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                    <Chip
                      size="small"
                      label={`Solicitante: ${ideaData?.solicitanteIdea || "—"}`}
                      sx={{ bgcolor: "#111624", color: "#ffffff", border: "1px solid #3a4457" }}
                    />
                    <Chip
                      size="small"
                      label={`Fecha: ${ideaData?.fechaRegistroIdea || "—"}`}
                      sx={{ bgcolor: "#111624", color: "#ffffff", border: "1px solid #3a4457" }}
                    />
                  </Stack>
                </Stack>
              )}
            </AccordionDetails>
          </Accordion>

          {/* ========= 2) Descripción (Detalle) ========= */}
          <Accordion disableGutters sx={{ bgcolor: "#0f141c", border: "1px solid #2c3442", borderRadius: 2, mb: 1.25 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#ffffff" }} />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <InfoOutlinedIcon fontSize="small" sx={{ color: "#ffffff" }} />
                <Typography variant="subtitle2" fontWeight={800} sx={{ color: "#e5e9f0" }}>Descripción</Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              {loadingDetail ? (
                <>
                  <Skeleton variant="text" sx={{ bgcolor: "#1a2030" }} />
                  <Skeleton variant="text" width="80%" sx={{ bgcolor: "#1a2030" }} />
                </>
              ) : (
                <Stack spacing={1} sx={{ color: "#e5e9f0" }}>
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1 }}>
                    <Box>
                      <Typography variant="overline" sx={{ color: "#cfd8e3" }}>Objetivo</Typography>
                      <Typography variant="body2" sx={{ color: "#ffffff" }}>{detailData?.objetivo || "—"}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="overline" sx={{ color: "#cfd8e3" }}>Alcance</Typography>
                      <Typography variant="body2" sx={{ color: "#ffffff" }}>{detailData?.alcance || "—"}</Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" }, gap: 1 }}>
                    <Chip size="small" label={`Prioridad: ${detailData?.codPrioridad ?? "—"}`} sx={{ bgcolor: "#111624", color: "#ffffff", border: "1px solid #3a4457" }} />
                    <Chip size="small" label={`Identificador: ${detailData?.identificador ?? "—"}`} sx={{ bgcolor: "#111624", color: "#ffffff", border: "1px solid #3a4457" }} />
                    <Chip size="small" label={`Origen: ${detailData?.codAreaOrigen ?? "—"}`} sx={{ bgcolor: "#111624", color: "#ffffff", border: "1px solid #3a4457" }} />
                    <Chip size="small" label={`Destino: ${detailData?.codAreaDestino ?? "—"}`} sx={{ bgcolor: "#111624", color: "#ffffff", border: "1px solid #3a4457" }} />
                  </Box>

                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" }, gap: 1 }}>
                    <Chip size="small" label={`Inicio Prog.: ${detailData?.fechaInicioProgramado || detailData?.fechaInicioProgramadoTicket || "—"}`} sx={{ bgcolor: "#101521", color: "#ffffff", border: "1px solid #2c3442" }} />
                    <Chip size="small" label={`Fin Prog.: ${detailData?.fechaFinProgramado || detailData?.fechaFinProgramadoTicket || "—"}`} sx={{ bgcolor: "#101521", color: "#ffffff", border: "1px solid #2c3442" }} />
                    <Chip size="small" label={`Inicio Real: ${detailData?.fechaInicioReal || "—"}`} sx={{ bgcolor: "#101521", color: "#ffffff", border: "1px solid #2c3442" }} />
                    <Chip size="small" label={`Fin Real: ${detailData?.fechaFinReal || "—"}`} sx={{ bgcolor: "#101521", color: "#ffffff", border: "1px solid #2c3442" }} />
                  </Box>
                </Stack>
              )}
            </AccordionDetails>
          </Accordion>

          {/* ========= 3) Análisis ========= */}
          <Accordion disableGutters sx={{ bgcolor: "#0f141c", border: "1px solid #2c3442", borderRadius: 2, mb: 1.25 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#ffffff" }} />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <AssessmentIcon fontSize="small" sx={{ color: "#ffffff" }} />
                <Typography variant="subtitle2" fontWeight={800} sx={{ color: "#e5e9f0" }}>Análisis</Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              {loadingAnalysis ? (
                <>
                  <Skeleton variant="text" sx={{ bgcolor: "#1a2030" }} />
                  <Skeleton variant="rectangular" height={48} sx={{ bgcolor: "#1a2030", borderRadius: 2 }} />
                </>
              ) : (
                <List dense>
                  {Array.isArray(analysisData) && analysisData.length > 0 ? (
                    analysisData.map((an) => (
                      <Box key={an.codTicketAnalisis} sx={{ mb: 1.25, color: "#e5e9f0" }}>
                        <Typography variant="body2" fontWeight={800} sx={{ color: "#ffffff" }}>{an.titulo || "—"}</Typography>
                        <Typography variant="body2" sx={{ color: "#e5e9f0", whiteSpace: "pre-wrap" }}>
                          {an.descripcionAnalisis || "—"}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: "wrap" }}>
                          <Chip size="small" label={`Inicio Real: ${an.fechaInicioReal || "—"}`} sx={{ bgcolor: "#101521", color: "#ffffff", border: "1px solid #2c3442" }} />
                          <Chip size="small" label={`Fin Real: ${an.fechaFinReal || "—"}`} sx={{ bgcolor: "#101521", color: "#ffffff", border: "1px solid #2c3442" }} />
                          <Chip size="small" label={`Registro: ${an.fechaRegistro || "—"}`} sx={{ bgcolor: "#101521", color: "#ffffff", border: "1px solid #2c3442" }} />
                          <Chip size="small" label={`Responsable: ${an.nomUsuarioResponsable || "—"}`} sx={{ bgcolor: "#111624", color: "#ffffff", border: "1px solid #3a4457" }} />
                        </Stack>
                        <Divider sx={{ borderColor: "#2c3442", mt: 1 }} />
                      </Box>
                    ))
                  ) : (
                    <Typography variant="body2" sx={{ color: "#cfd8e3" }}>Sin análisis registrados.</Typography>
                  )}
                </List>
              )}
            </AccordionDetails>
          </Accordion>

          {/* ========= 4) ÚNICO ESPACIO: Archivos & Evidencias (cards) ========= */}
          <Typography variant="subtitle2" fontWeight={800} sx={{ color: "#e5e9f0", mb: 1 }}>
            Archivos & Evidencias (Idea, Descripción, Análisis)
          </Typography>
          <Grid container spacing={1.25}>
            {unifiedFiles.length === 0 ? (
              <Grid item xs={12}>
                <Paper sx={{ p: 2, bgcolor: "#0f141c", border: "1px solid #2c3442", borderRadius: 3 }}>
                  <Typography variant="body2" sx={{ color: "#cfd8e3" }}>
                    Sin archivos ni imágenes adjuntas.
                  </Typography>
                </Paper>
              </Grid>
            ) : (
              unifiedFiles.map((f) => {
                const showImage = isImageUrl(f.url, f.ext);
                return (
                  <Grid item key={f.key} xs={12} sm={6} md={4}>
                    <Paper
                      sx={{
                        p: 1,
                        bgcolor: "#0f141c",
                        border: "1px solid #2c3442",
                        borderRadius: 3,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.75,
                      }}
                    >
                      <Box
                        sx={{
                          position: "relative",
                          bgcolor: "#0b0f14",
                          border: "1px solid #2c3442",
                          borderRadius: 2,
                          height: 160,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          overflow: "hidden",
                        }}
                      >
                        {showImage ? (
                          <img
                            src={f.url}
                            alt={f.name}
                            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                          />
                        ) : (
                          <Stack alignItems="center" spacing={1}>
                            <AttachFileIcon sx={{ color: "#ffffff", fontSize: 36 }} />
                            <Typography variant="caption" sx={{ color: "#cfd8e3" }}>
                              {f.ext ? f.ext.toUpperCase() : "Archivo"}
                            </Typography>
                          </Stack>
                        )}
                      </Box>

                      <Typography
                        variant="body2"
                        noWrap
                        title={f.name}
                        sx={{ color: "#ffffff", fontWeight: 700 }}
                      >
                        {f.name}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {showImage ? <ImageIcon sx={{ color: "#ffffff", fontSize: 18 }} /> : <AttachFileIcon sx={{ color: "#ffffff", fontSize: 18 }} />}
                        <Typography variant="caption" sx={{ color: "#cfd8e3" }}>
                          Origen: {f.source.toUpperCase()}
                        </Typography>
                      </Stack>

                      {f.url && (
                        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 0.5 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            href={f.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ borderColor: "#3a4457", color: "#ffffff" }}
                          >
                            {showImage ? "Ver" : "Abrir"}
                          </Button>
                        </Stack>
                      )}
                    </Paper>
                  </Grid>
                );
              })
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ borderTop: "1px solid #2c3442" }}>
          <Button onClick={closeTicketDetail} sx={{ color: "#ffffff" }}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===================== DRAWER: Actividad + Tareas ===================== */}
      <Drawer
        anchor="right"
        open={openActDrawer}
        onClose={closeActivityDetail}
        PaperProps={{ sx: { width: { xs: "100%", sm: 560 }, ...darkPaper } }}
      >
        <Box sx={{ p: 2.5, height: "100%", display: "flex", flexDirection: "column", gap: 1.25, color: "#e5e9f0" }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6" fontWeight={900} sx={{ color: "#ffffff" }}>
              Actividad #{actSel?.codTicketActividad}
            </Typography>
            {actSel && (
              <Chip
                size="small"
                label={statusChipColor(actSel.nomEstadoActividad).label}
                sx={{
                  bgcolor: statusChipColor(actSel.nomEstadoActividad).bg,
                  color: statusChipColor(actSel.nomEstadoActividad).tx,
                  border: `1px solid ${statusChipColor(actSel.nomEstadoActividad).bd}`,
                }}
              />
            )}
          </Stack>
          <Typography variant="body2" sx={{ color: "#e5e9f0" }}>
            {actSel?.nomActividad}
          </Typography>

          {/* Acciones rápidas (reproductor claro) */}
          {actSel && (
            <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: "wrap" }}>
              {/* Ocultar todo si finalizado */}
              {statusChipColor(actSel.nomEstadoActividad).key !== "final" && (
                <>
                  {/* Si no está corriendo */}
                  {!(actSel.fechaInicioReal && !actSel.fechaFinReal) && (
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<PlayArrowIcon sx={{ color: "#ffffff" }} />}
                      onClick={(e) => startActivity(actSel, e)}
                    >
                      Iniciar
                    </Button>
                  )}

                  {/* En proceso no pausado */}
                  {actSel.fechaInicioReal && !actSel.fechaFinReal && !pauseIdMap[actSel.codTicketActividad] && (
                    <>
                      <Button
                        size="small"
                        color="warning"
                        variant="contained"
                        startIcon={<PauseIcon sx={{ color: "#ffffff" }} />}
                        onClick={(e) => pauseActivity(actSel, e)}
                      >
                        Pausar
                      </Button>
                      <Button
                        size="small"
                        color="success"
                        variant="contained"
                        startIcon={<StopIcon sx={{ color: "#ffffff" }} />}
                        onClick={(e) => finishActivity(actSel, e)}
                      >
                        Finalizar
                      </Button>
                    </>
                  )}

                  {/* En pausa */}
                  {actSel.fechaInicioReal && !actSel.fechaFinReal && pauseIdMap[actSel.codTicketActividad] && (
                    <Button
                      size="small"
                      color="secondary"
                      variant="contained"
                      startIcon={<PlayArrowIcon sx={{ color: "#ffffff" }} />}
                      onClick={(e) => resumeActivity(actSel, e)}
                    >
                      Reanudar
                    </Button>
                  )}
                </>
              )}
            </Stack>
          )}

          {/* Fechas */}
          <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
            <Chip
              size="small"
              label={`Plan: ${actSel?.fechaInicioProgramado || "—"} → ${actSel?.fechaFinProgramado || "—"}`}
              sx={{ bgcolor: "#101521", color: "#ffffff", border: "1px solid #2c3442" }}
            />
            <Chip
              size="small"
              label={`Real: ${actSel?.fechaInicioReal || "—"} → ${actSel?.fechaFinReal || "—"}`}
              sx={{ bgcolor: "#101521", color: "#ffffff", border: "1px solid #2c3442" }}
            />
          </Stack>

          <Divider sx={{ my: 1, borderColor: "#2c3442" }} />

          {/* Tareas (solo si EN PROCESO) */}
          <Typography variant="overline" sx={{ color: "#cfd8e3" }}>
            Tareas
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} gap={1}>
            <TextField
              fullWidth
              size="small"
              placeholder="Nueva tarea…"
              value={newTask}
              onChange={(e) => setNewTask(e.target.value)}
              onKeyDown={onKeyDownAddTask}
              disabled={!canRegisterTasks(actSel)}
              sx={{ ...fieldOutline, "& .MuiInputBase-input": { color: "#ffffff" } }}
            />
            <Button
              variant="contained"
              onClick={createActTask}
              startIcon={<AddTaskIcon sx={{ color: "#ffffff" }} />}
              disabled={!canRegisterTasks(actSel)}
            >
              Registrar
            </Button>
            <Button
              variant="outlined"
              onClick={() => actSel && loadActTasks(actSel.codTicketActividad)}
              sx={{ borderColor: "#3a4457", color: "#ffffff" }}
            >
              Actualizar
            </Button>
          </Stack>

          <List dense sx={{ flex: 1, overflowY: "auto", mt: 1, borderTop: "1px dashed #2c3442" }}>
            {loadingTasks ? (
              <>
                {[...Array(3)].map((_, i) => (
                  <ListItem key={i}>
                    <Box sx={{ width: "100%" }}>
                      <Skeleton variant="text" width="60%" sx={{ bgcolor: "#1a2030" }} />
                      <Skeleton variant="text" width="30%" sx={{ bgcolor: "#1a2030" }} />
                    </Box>
                  </ListItem>
                ))}
              </>
            ) : (
              <>
                {actTasks.map((t) => (
                  <React.Fragment key={t.codTicketTarea}>
                    <ListItem
                      secondaryAction={
                        editTaskId === t.codTicketTarea ? (
                          <Stack direction="row" gap={1}>
                            <Button size="small" variant="contained" onClick={updateActTask}>
                              Guardar
                            </Button>
                            <Button
                              size="small"
                              onClick={() => {
                                setEditTaskId(null);
                                setEditTaskTxt("");
                              }}
                            >
                              Cancelar
                            </Button>
                          </Stack>
                        ) : (
                          <Tooltip title="Editar">
                            <IconButton
                              onClick={() => {
                                setEditTaskId(t.codTicketTarea);
                                setEditTaskTxt(t.descripcionTarea);
                              }}
                              sx={{ color: "#ffffff" }}
                            >
                              <EditIcon sx={{ color: "#ffffff" }} />
                            </IconButton>
                          </Tooltip>
                        )
                      }
                    >
                      {editTaskId === t.codTicketTarea ? (
                        <TextField
                          fullWidth
                          size="small"
                          value={editTaskTxt}
                          onChange={(e) => setEditTaskTxt(e.target.value)}
                          sx={{ ...fieldOutline, "& .MuiInputBase-input": { color: "#ffffff" } }}
                        />
                      ) : (
                        <ListItemText
                          primary={<Typography sx={{ color: "#ffffff" }}>{t.descripcionTarea}</Typography>}
                          secondary={<Typography variant="caption" sx={{ color: "#cfd8e3" }}>{t.fechaCreacion}</Typography>}
                        />
                      )}
                    </ListItem>
                    <Divider component="li" sx={{ borderColor: "#2c3442" }} />
                  </React.Fragment>
                ))}
                {actTasks.length === 0 && (
                  <ListItem>
                    <ListItemText
                      primary={<Typography variant="body2" sx={{ color: "#cfd8e3" }}>Sin tareas registradas.</Typography>}
                    />
                  </ListItem>
                )}
              </>
            )}
          </List>

          <Stack direction="row" justifyContent="flex-end" gap={1}>
            <Button onClick={closeActivityDetail} sx={{ color: "#ffffff" }}>
              Cerrar
            </Button>
          </Stack>
        </Box>
      </Drawer>
    </Box>
  );
};
